import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sparkle, TrendUp, Warning, Lightbulb } from "@phosphor-icons/react";

export default function AIInsightsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai/team-insights");
      setData(r.data);
      toast.success("AI insights generated");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-gradient-to-br from-sky-50 to-white border border-sky-100 rounded-lg p-5" data-testid="ai-insights-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-sky-600 text-white grid place-items-center">
            <Sparkle size={16} weight="fill" />
          </div>
          <div>
            <h4 className="font-semibold">AI Weekly Insights</h4>
            <p className="text-xs text-slate-500">Powered by GPT 5.2 · Team performance</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} data-testid="generate-insights-btn"
                className="text-sm bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md transition-colors">
          {loading ? "Generating…" : data ? "Regenerate" : "Generate"}
        </button>
      </div>

      {!data && !loading && (
        <p className="text-sm text-slate-500 mt-2">
          Click Generate to summarise top performers, bottlenecks and recommendations for this week.
        </p>
      )}

      {data && (
        <div className="space-y-3 mt-2" data-testid="ai-insights-content">
          {data.summary && <p className="text-sm text-slate-700 leading-relaxed italic">"{data.summary}"</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Panel icon={TrendUp} tone="emerald" title="Highlights" items={data.highlights} />
            <Panel icon={Warning} tone="amber" title="Bottlenecks" items={data.bottlenecks} />
            <Panel icon={Lightbulb} tone="sky" title="Recommendations" items={data.recommendations} />
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ icon: Icon, tone, title, items = [] }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md p-3`}>
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-widest text-${tone}-700 font-medium mb-2`}>
        <Icon size={14} weight="fill" /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.length === 0 && <li className="text-xs text-slate-400">—</li>}
        {items.map((t) => (
          <li key={t} className="text-xs text-slate-700 leading-snug flex gap-1.5">
            <span className={`w-1 h-1 rounded-full bg-${tone}-500 mt-1.5 shrink-0`} />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
